import { Link } from '@tanstack/react-router';
import { useActiveRealmAPIContext } from '@interxfi/api-client';
import { useIxcRegistrationV1AccountQueryServiceAccountGet } from '@interxfi/api-client/generated/hooks/ixc-registration-v1-account-query-service-account-get';
import { TextLink } from '@interxfi/design-system/components/link/text-link/text-link';
import { Skeleton } from '@interxfi/design-system/components/loading/skeleton';
import type { TypographyProps } from '@interxfi/design-system/components/typography';
import { IxcRegistrationV1Purpose } from '@interxfi/types';
import { REGISTRATION_ACCOUNT_BASE_PATH } from './registration-account-routing';

const baseTypographyProps = {
  size: '0.875rem',
  lineHeight: '1.5rem',
  inheritance: 'current',
  wrap: 'ellipsis',
} as const satisfies TypographyProps;

export const inactiveBreadcrumbTypographyProps = {
  ...baseTypographyProps,
  tone: 'neutral-dk',
} as const satisfies TypographyProps;

interface AccountPurposeBreadcrumbProps {
  accountId: string;
}

export function AccountPurposeBreadcrumb({ accountId }: AccountPurposeBreadcrumbProps) {
  const { data, error, isPending } = useIxcRegistrationV1AccountQueryServiceAccountGet({ accountId });
  const showPending = Boolean(isPending || error);
  const { activeTenantIsClearing } = useActiveRealmAPIContext();

  if (showPending) {
    return <Skeleton charCount={12} />;
  }

  if (!data?.account?.data?.spec || !('platform' in data.account.data.spec) || data.account.data.purpose === IxcRegistrationV1Purpose.Special) {
    return (
      <TextLink LinkElement={Link} size="small" to={REGISTRATION_ACCOUNT_BASE_PATH} typography={inactiveBreadcrumbTypographyProps} underline="hover">
        Registration
      </TextLink>
    );
  }

  if (activeTenantIsClearing) {
    return (
      <TextLink
        LinkElement={Link}
        size="small"
        // TODO: link to firm accounts page when there's a mechanism to do so?
        to="/clearing/operations/account/correspondent"
        typography={inactiveBreadcrumbTypographyProps}
        underline="hover"
      >
        Correspondent Accounts
      </TextLink>
    );
  }

  return (
    <TextLink LinkElement={Link} size="small" to="/dashboard" typography={inactiveBreadcrumbTypographyProps} underline="hover">
      Dashboard
    </TextLink>
  );
}
